import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { supabase } from '../lib/supabase.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { GAME_DEFAULTS } from '../lib/constants.js'
import CircuitTrack from '../components/ui/CircuitTrack.jsx'
import HostHeader from '../components/ui/HostHeader.jsx'
import HostSideNav from '../components/ui/HostSideNav.jsx'
import RaceTour from '../components/ui/RaceTour.jsx'
import { useTour } from '../hooks/useTour.js'
import * as musicPlayer from '../lib/music.js'

const POLL_MS = 2000

function rankKarts(karts, nbCases) {
  return [...karts].sort((a, b) => {
    const aF = a.statut === 'finished'
    const bF = b.statut === 'finished'
    if (aF && !bF) return -1
    if (!aF && bF) return 1
    if (aF && bF) return new Date(a.finished_at ?? 0) - new Date(b.finished_at ?? 0)
    const sa = (a.tour_actuel - 1) * nbCases + a.position_case
    const sb = (b.tour_actuel - 1) * nbCases + b.position_case
    return sb - sa
  })
}

function formatRank(rank) {
  return rank === 0 ? '1er' : `${rank + 1}e`
}

// ── Classement vertical (sidebar droite) ──────────────────────
function RankingBar({ karts, nbCases }) {
  const ranked = useMemo(() => rankKarts(karts, nbCases), [karts, nbCases])

  return (
    <div className="flex flex-col items-center gap-0.5 px-1">
      {ranked.map((k, rank) => (
        <div
          key={k.id}
          className="flex flex-col items-center py-1.5 w-full border-b border-white/5 last:border-0"
        >
          <span className="text-[9px] text-white/65 font-semibold">{formatRank(rank)}</span>
          <TwemojiImg emoji={k.type_vehicule} size={28} />
          <span className="text-[9px] text-white font-medium truncate max-w-full text-center leading-tight">
            {k.pseudo}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Page principale ───────────────────────────────────────────
export default function RacePage() {
  const { code }  = useParams()
  const navigate  = useNavigate()

  const [game, setGame]               = useState(null)
  const [karts, setKarts]             = useState([])
  const [nbCases, setNbCases]         = useState(20)
  const [nbBonus, setNbBonus]         = useState(4)
  const [imageUrl, setImageUrl]       = useState(null)
  const [pieges, setPieges]           = useState([])
  const [attackEvents, setAttackEvents] = useState([])
  const [showTour, setShowTour]       = useState(false)
  const [showInactiveTooltip, setShowInactiveTooltip] = useState(false)
  const [isHost, setIsHost]           = useState(false)
  const [musicEnabled, setMusicEnabled] = useState(() => musicPlayer.isEnabled())
  const pollingRef         = useRef(null)
  const prevAttacksRef     = useRef({}) // { kartId → attaque_recue.ts }
  const attacksInitialized = useRef(false)
  const tourTriggeredRef   = useRef(false)

  const { shouldShow: tourShouldShow, markDone: markTourDone } = useTour('race')

  // ❄️ exclu : le Gel cible tous les joueurs simultanément → N-1 projectiles
  // laisseraient une traînée visuelle permanente. L'effet est déjà visible via
  // le ❄️ dans les bulles des karts gelés (CircuitTrack → activeEffectEmoji).
  const FLYING_ATTACKS = ['🐚', '💣', '🪝']

  const fetchPositions = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/games/${code}/positions`)
      setKarts(data.karts)
      setNbCases(data.nb_cases)
      setNbBonus(data.nb_bonus)
      setPieges(data.pieges ?? [])
      if (data.image_url) setImageUrl(data.image_url)

      // Au premier appel, on mémorise les timestamps existants sans créer d'événements,
      // pour éviter de rejouer des attaques passées si on revient sur cet écran.
      if (!attacksInitialized.current) {
        data.karts.forEach(k => {
          if (k.attaque_recue) prevAttacksRef.current[k.id] = k.attaque_recue.ts
        })
        attacksInitialized.current = true
        return
      }

      // Détecter les nouvelles attaques
      const newEvents = []
      data.karts.forEach(k => {
        if (!k.attaque_recue) return
        const prevTs = prevAttacksRef.current[k.id]
        const newTs  = k.attaque_recue.ts
        if (prevTs === newTs) return
        prevAttacksRef.current[k.id] = newTs
        if (!FLYING_ATTACKS.includes(k.attaque_recue.emoji)) return
        const attacker = data.karts.find(a => a.pseudo === k.attaque_recue.pseudo_attaquant)
        if (!attacker) return
        newEvents.push({
          id:         `${k.id}_${newTs}`,
          emoji:      k.attaque_recue.emoji,
          fromKartId: attacker.id,
          toKartId:   k.id,
        })
      })
      if (newEvents.length > 0)
        setAttackEvents(prev => [...prev.slice(-10), ...newEvents])
    } catch (_) {}
  }, [code])

  useEffect(() => {
    apiFetch(`/api/games/${code}`)
      .then(data => {
        setGame(data)
        setImageUrl(data.decks?.themes?.image_url ?? null)
      })
      .catch(() => navigate('/', { replace: true }))
  }, [code])

  useEffect(() => {
    fetchPositions()
    pollingRef.current = setInterval(fetchPositions, POLL_MS)
    return () => clearInterval(pollingRef.current)
  }, [fetchPositions])

  useEffect(() => {
    if (!game || game.statut !== 'playing' || !tourShouldShow || tourTriggeredRef.current) return
    tourTriggeredRef.current = true
    const timer = setTimeout(() => setShowTour(true), 400)
    return () => clearTimeout(timer)
  }, [game?.statut, tourShouldShow])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && game?.hote_id) {
        setIsHost(session.user.id === game.hote_id)
      }
    })
  }, [game?.hote_id])

  useEffect(() => {
    if (!musicPlayer.hasAudio()) return
    const s = game?.statut
    if (s === 'playing') musicPlayer.play()
    else if (s === 'paused') musicPlayer.pause()
    else musicPlayer.stop()
  }, [game?.statut])

  useEffect(() => {
    return () => { musicPlayer.stop() }
  }, [])

  function handleGameUpdate(type, data) {
    if (type === 'end') { navigate(`/podium/${code}`, { replace: true }); return }
    if (data?.statut)   setGame(g => ({ ...g, statut: data.statut }))
  }

  function handleMusicToggle() {
    const musicUrl = game?.decks?.musiques?.url ?? null
    if (!musicPlayer.hasAudio() && musicUrl) {
      musicPlayer.initAudio(musicUrl, true)
      musicPlayer.play()
      setMusicEnabled(true)
      return
    }
    const enabled = musicPlayer.toggle()
    setMusicEnabled(enabled)
  }

  const statut        = game?.statut ?? 'playing'
  const finished      = karts.filter(k => k.statut === 'finished').length
  const inactiveKarts  = karts.filter(k => k.is_inactive)
  const inactiveCount  = inactiveKarts.length

  const bgStyle = imageUrl
    ? { backgroundImage: `url(${imageUrl})` }
    : { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }

  return (
    <div className="h-[100dvh] flex flex-col select-none bg-gray-900">
      {showTour && <RaceTour onDone={() => { setShowTour(false); markTourDone() }} />}

      <HostHeader
        code={code}
        deckTitle={game?.decks?.titre ?? '…'}
        statut={statut}
        playerCount={karts.length}
        maxPlayers={game?.config?.max_joueurs ?? GAME_DEFAULTS.MAX_PLAYERS}
        onGameUpdate={handleGameUpdate}
      />

      <div className={`flex flex-1 min-h-0 ${imageUrl ? 'bg-contain sm:bg-cover bg-center sm:bg-top bg-no-repeat' : ''}`} style={bgStyle}>
        <HostSideNav
          code={code}
          active="race"
          statut={statut}
          deckTitle={game?.decks?.titre ?? ''}
          onGameUpdate={handleGameUpdate}
        />

        {/* Zone circuit — SVG remplit tout l'espace disponible */}
        <div className="flex-1 min-h-0 p-2 relative">
          <div className="absolute top-3 right-4 z-10 flex flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              {inactiveCount > 0 && (
                <div
                  className="relative"
                  onMouseEnter={() => setShowInactiveTooltip(true)}
                  onMouseLeave={() => setShowInactiveTooltip(false)}
                >
                  <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2 flex items-center gap-2 text-white text-sm font-medium cursor-default">
                    <span>💤</span>
                    <span>{inactiveCount}</span>
                  </div>
                  {showInactiveTooltip && (
                    <div className="absolute top-full right-0 mt-2 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 text-xs text-white whitespace-nowrap pointer-events-none z-[200]">
                      <span className="font-medium">{inactiveCount} kart{inactiveCount > 1 ? 's' : ''} inactif{inactiveCount > 1 ? 's' : ''} :</span>
                      {' '}{inactiveKarts.map(k => k.pseudo).join(', ')}
                    </div>
                  )}
                </div>
              )}
              {isHost && game?.decks?.musiques?.url && statut !== 'finished' && (
                <button
                  onClick={handleMusicToggle}
                  title={musicEnabled ? 'Couper la musique' : 'Activer la musique'}
                  className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-2 flex items-center justify-center text-sm text-white hover:bg-gray-900 transition"
                >
                  <span>{musicEnabled ? '🔊' : '🔇'}</span>
                </button>
              )}
            </div>
            {finished > 0 && (
              <div className="bg-green-500/80 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {finished} arrivé{finished > 1 ? 's' : ''}
              </div>
            )}
          </div>
          <CircuitTrack
            nbCases={nbCases}
            nbBonus={nbBonus}
            karts={karts}
            kartSize={24}
            pieges={pieges}
            attackEvents={attackEvents}
            className="w-full h-full"
          />
          {statut === 'paused' && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl px-12 py-8 flex flex-col items-center gap-3">
                <span className="text-7xl">⏸️</span>
                <span className="text-white font-bold text-3xl tracking-widest">PAUSE</span>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar classement droite — masquée sur mobile */}
        <div className="hidden sm:flex w-16 bg-gray-900 border-l border-white/10 shrink-0 flex-col overflow-y-auto py-2">
          <p className="text-white/25 text-[9px] uppercase tracking-widest text-center mb-1">
            Class.
          </p>
          {karts.length === 0 ? (
            <p className="text-white/30 text-[9px] text-center px-1 mt-2">…</p>
          ) : (
            <RankingBar karts={karts} nbCases={nbCases} />
          )}
        </div>

      </div>
    </div>
  )
}
